import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './SimpleKeycloakProvider';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Avatar,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import {
  LockOutlined,
  Home,
  Logout as LogoutIcon,
  Shield,
} from '@mui/icons-material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
  const { authenticated, hasAnyRole, logout } = useAuth();

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !hasAnyRole(roles)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Avatar
              sx={{
                m: 'auto',
                mb: 3,
                width: 80,
                height: 80,
                bgcolor: 'error.main',
              }}
            >
              <LockOutlined sx={{ fontSize: 40 }} />
            </Avatar>

            <Typography variant="h3" fontWeight="bold" gutterBottom color="error">
              Access Denied
            </Typography>

            <Typography variant="h6" color="text.secondary" paragraph>
              You do not have permission to access this resource
            </Typography>

            <Divider sx={{ my: 3 }}>
              <Chip label="INSUFFICIENT PRIVILEGES" color="error" size="small" />
            </Divider>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Required Roles:
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mt: 1 }}>
                {roles.map(role => (
                  <Chip
                    key={role}
                    label={role.toUpperCase()}
                    color="warning"
                    variant="outlined"
                    icon={<Shield />}
                  />
                ))}
              </Stack>
            </Box>

            <Stack spacing={2}>
              <Button
                variant="contained"
                size="large"
                onClick={() => window.location.href = '/'}
                startIcon={<Home />}
                sx={{
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  color: 'white',
                }}
              >
                Return to Home
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={logout}
                startIcon={<LogoutIcon />}
              >
                Sign Out & Try Another Account
              </Button>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 4, display: 'block' }}>
              If you believe this is an error, please contact your system administrator
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return <>{children}</>;
};